import { FC, useMemo } from "react";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import ActiveLink from "components/base/active-link/ActiveLink";
import classNames from "classnames";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import { ProfileItemDataType } from "hooks";
import Button from "components/base/button/Button";
import styles from "./account-menu-item.module.sass";

type AccountMenuItemProps = {
    data: ProfileItemDataType;
    variant?: "compact" | "tab" | "default";
    showExtraIcon?: boolean;
    isSubItem?: boolean;
    onClick?: () => void;
};

const AccountMenuItem: FC<AccountMenuItemProps> = ({
    data,
    variant = "default",
    showExtraIcon = true,
    isSubItem = false,
    onClick,
}) => {
    if (!data) {
        console.warn("menu item data is not defined");
        return null;
    }

    const { icon, iconPath, extraIconPath, text, href, includes, action, dataTestId, isButton } =
        data;

    const content = useMemo(() => {
        const isTabVariant = variant === "tab";

        return (
            <>
                <Svg
                    src={
                        iconPath ||
                        (isTabVariant
                            ? `/${AssetsDirectory.Common}/${AssetsDirectory.Tablet}/${AssetsDirectory.Profile}/${icon}`
                            : `/${AssetsDirectory.Mobile}/${AssetsDirectory.ProfileCompact}/${icon}`)
                    }
                    className="svg-icon"
                    prefixAppName={!isTabVariant && !iconPath}
                />
                <p>
                    <Translate
                        tid={text.tid}
                        namespace={text.namespace || TranslationScopes.UserAccount}
                    />
                </p>
                {extraIconPath && showExtraIcon && (
                    <Svg src={extraIconPath} className="svg-icon-unverified" />
                )}
            </>
        );
    }, [icon, iconPath, extraIconPath, showExtraIcon, text, variant]);

    return (
        <Button
            type="button"
            variant="custom"
            onClick={() => {
                action?.();
                onClick?.();
            }}
            className={classNames(
                styles.item,
                isSubItem && styles.secondary,
                variant === "compact" && styles.compact,
                variant === "tab" && styles.tab,
            )}
            data-testid={dataTestId}>
            {isButton ? (
                content
            ) : (
                <ActiveLink
                    href={href || ""}
                    includes={includes}
                    activeClassName={styles.itemActive}
                    className={styles.linkItem}>
                    {content}
                </ActiveLink>
            )}
        </Button>
    );
};

export default AccountMenuItem;
